import { useCallback, useEffect, useState } from 'react';

import RestaurantSettingsService from '../../features/restaurant/settings/RestaurantSettingsService';
import { RestaurantKitchenCompletionMode } from '../../features/restaurant/settings/RestaurantSettingsTypes';

/**
 * Comportamiento de cocina configurable por sucursal (pestaña "Cocina" de
 * Configuración → Restaurante). Solo lo que el tablero de cocina necesita
 * conocer.
 */
export interface RestaurantKitchenConfig {
  kitchenDisplayEnabled: boolean;
  kitchenPrinterEnabled: boolean;
  preparationTimerEnabled: boolean;
  orderCompletionMode: RestaurantKitchenCompletionMode;
  autoAdvanceItemsByTime: boolean;
  maxOrdersInPreparation: number;
  allowStationTransfer: boolean;
  parallelPreparationEnabled: boolean;
}

/** Valores por defecto (coinciden con los de la entidad del backend). */
export const KITCHEN_CONFIG_DEFAULTS: RestaurantKitchenConfig = {
  kitchenDisplayEnabled: true,
  kitchenPrinterEnabled: true,
  preparationTimerEnabled: true,
  orderCompletionMode: 'MANUAL',
  autoAdvanceItemsByTime: false,
  maxOrdersInPreparation: 0,
  allowStationTransfer: true,
  parallelPreparationEnabled: true,
};

/**
 * Lee la configuración de cocina de la sucursal desde
 * `GET /api/restaurant/settings/branches/{branchId}` y expone solo los toggles
 * de comportamiento. Mientras carga (o si falla) devuelve los valores por
 * defecto, de modo que el tablero nunca queda bloqueado.
 */
const useRestaurantKitchenConfig = (branchId: number | '') => {
  const [config, setConfig] = useState<RestaurantKitchenConfig>(
    KITCHEN_CONFIG_DEFAULTS
  );
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) {
      setConfig(KITCHEN_CONFIG_DEFAULTS);
      return;
    }

    setLoading(true);

    try {
      const response = await RestaurantSettingsService.getByBranch(
        Number(branchId)
      );
      const kitchen = response?.data?.kitchen;

      if (kitchen) {
        setConfig({
          kitchenDisplayEnabled: kitchen.kitchenDisplayEnabled,
          kitchenPrinterEnabled: kitchen.kitchenPrinterEnabled,
          preparationTimerEnabled: kitchen.preparationTimerEnabled,
          orderCompletionMode: kitchen.orderCompletionMode ?? 'MANUAL',
          autoAdvanceItemsByTime: kitchen.autoAdvanceItemsByTime ?? false,
          maxOrdersInPreparation: kitchen.maxOrdersInPreparation ?? 0,
          allowStationTransfer: kitchen.allowStationTransfer,
          parallelPreparationEnabled: kitchen.parallelPreparationEnabled,
        });
      } else {
        setConfig(KITCHEN_CONFIG_DEFAULTS);
      }
    } catch {
      setConfig(KITCHEN_CONFIG_DEFAULTS);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { config, loading, reload: load };
};

export default useRestaurantKitchenConfig;
